import React from 'react';
import styled from 'styled-components';
import { Tile } from './Tile';
import { dndTypes, dragLayerRadius } from '../helpers/constants';
import { TileGroupDropItem, TileDropItem, Orientation } from '../types';

import { XYCoord, useDragLayer } from 'react-dnd';
import { ActiveDropTarget } from '../containers/ActiveDropTarget';
import { MagnifierContainer } from '../containers/MagnifierContainer';
const Layer = styled.div`
    position: fixed;
    pointer-events: none;
    z-index: 100;
    left: 0;
    top: 0;
    width: 100%;
    height: 100%;
`;

const Tiles =
    styled.span <
    { orientation: Orientation } >
    `
    display: flex;
    flex-direction: ${(props) => (props.orientation === Orientation.HORIZONTAL ? 'row' : 'column')};
`;

const BasicItemContainer =
    styled.div.attrs<{ currentOffset: XYCoord | null }>((props) => {
        return props.currentOffset
            ? {
                  style: {
                      transform: `translate(${props.currentOffset.x + 30}px, ${
                          props.currentOffset.y - 30
                      }px)`,
                  },
              }
            : {};
    }) <
    { currentOffset: XYCoord | null } >
    ``;

export const ItemContainer =
    styled.div.attrs<{ windowWidth: number, currentOffset: XYCoord | null }>((props) => {
        const { x: currentX, y: currentY } = props.currentOffset || { x: 0, y: 0 };

        const deltaX = dragLayerRadius / 8;
        const deltaY = dragLayerRadius / 8 + dragLayerRadius * 2;
        let computedDeltaX = deltaX;
        let computedDeltaY = deltaY;
        const hypotenuse = Math.pow(
            Math.pow(deltaY - dragLayerRadius, 2) + Math.pow(deltaX + dragLayerRadius, 2),
            1 / 2,
        );

        const totalWidth = currentX + deltaX + dragLayerRadius * 2;

        //console.log(totalWidth, props.windowWidth);

        // If the magnifier is getting cutoff
        // then shrink it
        if (totalWidth > props.windowWidth) {
            computedDeltaX = deltaX - (totalWidth - props.windowWidth);
            computedDeltaY =
                Math.pow(
                    Math.pow(hypotenuse, 2) - Math.pow(dragLayerRadius + computedDeltaX, 2),
                    1 / 2,
                ) + dragLayerRadius;

            const totalHeight = currentY - computedDeltaY;
        }

        const totalHeight = currentY - computedDeltaY;

        if (totalHeight < 0) {
            computedDeltaY = computedDeltaY + totalHeight;
            computedDeltaX =
                Math.pow(
                    Math.pow(hypotenuse, 2) - Math.pow(computedDeltaY - dragLayerRadius, 2),
                    1 / 2,
                ) - dragLayerRadius;

            const totalWidth = currentX + computedDeltaX + dragLayerRadius * 2;
            if (totalWidth > props.windowWidth) {
                computedDeltaX = computedDeltaX - (totalWidth - props.windowWidth);
            }
        }

        // move this around based on the window width
        return props.currentOffset
            ? {
                  style: {
                      transform: `translate(${currentX + computedDeltaX}px, ${
                          currentY - computedDeltaY
                      }px)`,
                  },
              }
            : {};
    }) <
    { windowWidth: number, currentOffset: XYCoord | null } >
    `
    width: ${dragLayerRadius * 2}px;
    height: ${dragLayerRadius * 2}px;
    border-radius: 50%;
    border: 1px solid black;
    overflow: hidden;
    background-color: white;
    position: absolute;
    top: 0;
`;

export const CustomDragLayer: React.FC<{
    component: (
        iniitialOffset: XYCoord | null,
        initialClientOffset: XYCoord | null,
        currentOffset: XYCoord | null,
    ) => React.ReactNode,
}> = ({ component }) => {
    const { dropTarget } = ActiveDropTarget.useContainer();
    const { magnifier } = MagnifierContainer.useContainer();
    const { isDragging, item, initialOffset, initialClientOffset, currentOffset } = useDragLayer(
        (monitor) => {
            const item: TileGroupDropItem | TileDropItem = monitor.getItem();
            return {
                item,
                itemType: monitor.getItemType(),
                initialClientOffset: monitor.getInitialClientOffset(),
                initialOffset: monitor.getInitialSourceClientOffset(),
                currentOffset: monitor.getSourceClientOffset(),
                isDragging: monitor.isDragging(),
            };
        },
    );

    const renderItem = () => {
        if (item) {
            if (dropTarget === dndTypes.BOARD && magnifier) {
                return component(initialOffset, initialClientOffset, currentOffset);
            } else if (dropTarget === dndTypes.HAND) {
                if (item.type !== dndTypes.TILE_GROUP) {
                    // TODO: If we are dropping multiple tiles, from the board into the hand,
                    // we need to show this
                    return (
                        <BasicItemContainer currentOffset={currentOffset}>
                            <Tile id={item.id} dragType={dndTypes.TILE_FROM_HAND} />
                        </BasicItemContainer>
                    );
                } else if (item.type === dndTypes.TILE_GROUP) {
                    return (
                        <BasicItemContainer currentOffset={currentOffset}>
                            <Tiles orientation={item.orientation}>
                                {item.tiles.map((id) => {
                                    return (
                                        <Tile key={id} id={id} dragType={dndTypes.TILE_FROM_HAND} />
                                    );
                                })}
                            </Tiles>
                        </BasicItemContainer>
                    );
                }
            }
        }
    };

    const itemToRender = renderItem();
    if (!isDragging) {
        return null;
    }

    return <Layer>{itemToRender}</Layer>;
};
